// src/admin/pages/AdminOrders.jsx
import { useEffect, useMemo, useState } from "react";
import axiosAdmin from "../axiosAdmin";
import { StatusBadge } from "../components/StatusBadge";
import { useNavigate } from "react-router-dom";
import { 
  ShoppingCart, 
  Clock, 
  Hourglass, 
  Truck, 
  CheckCircle, 
  XCircle, 
  Filter, 
  Search,
  ChevronLeft,
  ChevronRight,
  AlertTriangle
} from "lucide-react";

const PAGE_SIZE = 50; // Taille de page synchronisée avec le backend

// =========================
// MAPPING STATUT → BADGE + COULEURS (INTACT)
// =========================
const getStatusClasses = (status) => {
    switch (status) {
        case 'en_attente':
            return { active: false, label: "En attente", icon: <Hourglass size={14} className="mr-1.5"/>, colorClass: "bg-gray-100 text-gray-700 border-gray-300" };
        case 'en_preparation':
            return { active: false, label: "En préparation", icon: <Clock size={14} className="mr-1.5"/>, colorClass: "bg-amber-100 text-amber-700 border-amber-300" };
        case 'prete':
            return { active: true, label: "Prête", icon: <CheckCircle size={14} className="mr-1.5"/>, colorClass: "bg-blue-100 text-blue-700 border-blue-300" };
        case 'livree':
            return { active: true, label: "Livrée", icon: <Truck size={14} className="mr-1.5"/>, colorClass: "bg-green-100 text-green-700 border-green-300" };
        case 'annulee':
            return { active: false, label: "Annulée", icon: <XCircle size={14} className="mr-1.5"/>, colorClass: "bg-red-100 text-red-700 border-red-300" };
        default:
            return { active: false, label: status, icon: <Clock size={14} className="mr-1.5"/>, colorClass: "bg-gray-200 text-gray-800 border-gray-400" };
    }
};

const STATUS_META = {
    en_attente: { label: "En attente", color: "bg-gray-100 text-gray-700", icon: Hourglass },
    en_preparation: { label: "En préparation", color: "bg-amber-100 text-amber-700", icon: Clock },
    prete: { label: "Prête", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
    livree: { label: "Livrées", color: "bg-green-100 text-green-700", icon: Truck },
    annulee: { label: "Annulées", color: "bg-red-100 text-red-700", icon: XCircle },
    total: { label: "Total", color: "bg-indigo-100 text-indigo-700", icon: ShoppingCart }
};


export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [anomalies, setAnomalies] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate(); 

  // filtres (INTACT)
  const [status, setStatus] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [orderSearch, setOrderSearch] = useState("");

  // pagination (INTACT)
  const [page, setPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0); 
  const [totalPages, setTotalPages] = useState(1);


  // =========================
  // FETCH DATA (INTACT)
  // =========================
  const fetchAll = async () => {
    try {
      setLoading(true);
      setError("");

      const [ordersRes, statsRes, anomaliesRes] = await Promise.all([
        axiosAdmin.get("/admin/orders", {
          params: {
            status: status || undefined,
            merchantId: merchantId || undefined,
            // ⚠️ AJOUT : Envoi des paramètres de pagination au serveur
            page: page, 
            limit: PAGE_SIZE,
          },
        }),
        axiosAdmin.get("/admin/orders/stats"),
        axiosAdmin.get("/admin/orders/anomalies"),
      ]);

      setOrders(ordersRes.data.orders || []);
      setStats(statsRes.data || null);
      setAnomalies(anomaliesRes.data.stuckOrders || []);
      
      // ⚠️ AJOUT : Mise à jour des totaux de pagination depuis la réponse du serveur
      if (ordersRes.data.pagination) {
          setTotalOrders(ordersRes.data.pagination.total);
          setTotalPages(ordersRes.data.pagination.pages);
      }

    } catch (err) {
      console.error("❌ AdminOrders:", err);
      setError("Impossible de charger les commandes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ⚠️ MODIFICATION : Recharger quand les filtres OU la page changent
    fetchAll();
  }, [status, merchantId, page]); 
  
  // =========================
  // LOGIQUE DE FILTRAGE ID COMMANDE (INTACT)
  // =========================
  const filteredOrders = useMemo(() => {
    // Si la recherche est vide, on utilise les 50 ordres reçus du serveur
    if (!orderSearch.trim()) return orders;

    const term = orderSearch.trim().toLowerCase();

    // On filtre les 50 ordres de la page courante
    return orders.filter((o) => {
      return (
        o._id?.toLowerCase().includes(term) ||
        o.orderNumber?.toLowerCase().includes(term)
      );
    });
  }, [orders, orderSearch]);

  // =========================
  // HANDLERS (INTACT)
  // =========================
  const handlePageChange = (newPage) => {
      // ⚠️ Nous devons d'abord revenir à la page 1 si la recherche est active
      if (orderSearch.trim()) {
          setOrderSearch('');
      }
      setPage(newPage);
  }

  // =========================
  // RENDER (VISUEL ORGANISÉ)
  // =========================
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-gray-400">
        Chargement des commandes...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 m-8 text-center text-red-500 border border-red-100 bg-red-50 rounded-xl">
        {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 space-y-8 font-sans bg-gray-50/50">
      
      {/* 1. EN-TÊTE DE SECTION (INTACT) */}
      <div className="flex items-center justify-between">
        <h1 className="flex items-center text-3xl font-extrabold tracking-tight text-gray-900">
          <ShoppingCart className="mr-3 text-gray-400" size={28} />
          Supervision des Commandes
        </h1>
      </div>

      {/* ================= STATS (INTACT) ================= */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {Object.keys(stats).map(key => {
            const meta = STATUS_META[key] || STATUS_META.total;
            if (stats[key] === undefined || (key === 'total' && stats[key] === 0 && totalOrders === 0)) return null;

            return (
              <Stat 
                key={key} 
                label={meta.label} 
                value={stats[key]} 
                colorClass={meta.color}
                Icon={meta.icon}
              />
            );
          })}
        </div>
      )}

      {/* ================= ANOMALIES (INTACT) ================= */}
      {anomalies.length > 0 && (
        <div className="flex items-center gap-3 p-5 bg-red-100 border-2 border-red-400 shadow-md rounded-xl">
          <AlertTriangle size={24} className="flex-shrink-0 text-red-700" />
          <div>
            <p className="text-lg font-extrabold text-red-800">
              {anomalies.length} Anomalie(s) détectée(s)
            </p>
            <p className="text-sm text-red-700">
              {anomalies.length} commande(s) bloquée(s) depuis plus de 2 heures.
            </p>
          </div>
        </div>
      )}

      {/* ================= FILTRES (INTACT) ================= */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-white border border-gray-200 shadow-sm rounded-xl">
        <Filter size={20} className="text-gray-500" />
        
        <select
          className="px-4 py-2 text-sm transition border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-blue-500"
          value={status}
          onChange={(e) => {
              setStatus(e.target.value);
              setPage(1); // Retourne à la première page lors du changement de filtre principal
          }}
        >
          <option value="">Tous les statuts</option>
          <option value="en_attente">En attente</option>
          <option value="en_preparation">En préparation</option>
          <option value="prete">Prête</option>
          <option value="livree">Livrée</option>
          <option value="annulee">Annulée</option>
        </select>

        <input
          className="px-4 py-2 text-sm transition border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-blue-500"
          placeholder="ID Marchand (optionnel)"
          value={merchantId}
          onChange={(e) => {
              setMerchantId(e.target.value);
              setPage(1); // Retourne à la première page lors du changement de filtre principal
          }}
        />

        <div className="relative flex-grow max-w-sm">
            <Search size={16} className="absolute text-gray-400 -translate-y-1/2 left-3 top-1/2" />
            <input
            className="w-full px-4 py-2 pl-10 text-sm transition border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-blue-500"
            placeholder="ID ou numéro de commande (page actuelle)"
            value={orderSearch}
            onChange={(e) => {
                setOrderSearch(e.target.value);
            }}
            />
        </div>

      </div>

      {/* ================= TABLE (Correction structurelle) ================= */}
      <div className="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs font-semibold tracking-wider text-gray-500 uppercase border-b border-gray-100 bg-gray-50/80">
              <tr>
                <th className="px-6 py-4">Commande</th>
                <th className="px-6 py-4">Marchand</th>
                <th className="px-6 py-4">Total payé</th>
                <th className="px-6 py-4">Statut</th>
                <th className="px-6 py-4">Date & Heure</th>
                <th className="px-6 py-4">Actions</th> {/* Colonne séparée pour le bouton */}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {filteredOrders.map((o) => { // Utilisation de filteredOrders pour la recherche rapide
                const meta = getStatusClasses(o.status);

                return (
                  <tr key={o._id} className="transition-colors hover:bg-gray-50">
                    
                    {/* COMMANDE ID/NUMBER (Correction: Suppression de l'ID gris) */}
                    <td className="px-6 py-4 font-mono text-xs text-gray-700">
                      <span className="block font-bold text-gray-900">#{o.orderNumber || o._id.slice(-6)}</span>
                      {/* Ligne supprimée : <span className="text-gray-500">({o._id.slice(0, 8)}...)</span> */}
                    </td>
                    
                    {/* MARCHAND */}
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {o.merchantId?.name || "Marchand inconnu"}
                    </td>

                    {/* TOTAL */}
                    <td className="px-6 py-4 font-bold text-gray-900">
                      {o.total} <span className="text-xs font-normal text-gray-400">MRU</span>
                    </td>
                    
                    {/* STATUT (Badge stylé) */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-bold border rounded-lg ${meta.colorClass}`}>
                          {meta.icon}
                          {meta.label}
                      </span>
                    </td>
                    
                    {/* DATE & HEURE */}
                    <td className="px-6 py-4 text-xs font-medium text-gray-500">
                      {new Date(o.createdAt).toLocaleString()}
                    </td> {/* Fermeture correcte du td */}
                    
                    {/* ACTIONS (Correction: Placement dans sa propre cellule <td>) */}
                    <td className="px-6 py-4">
                      <button
                        onClick={() =>
                          navigate(`/admin/orders/${o.orderNumber}`)
                        }
                        className="px-3 py-1 text-xs font-bold text-blue-600 rounded-lg bg-blue-50 hover:bg-blue-100"
                      >
                        Détails
                      </button>
                    </td>

                  </tr>
                );
              })}

              {filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400"> {/* Correction du colspan à 6 */}
                    <div className="flex flex-col items-center">
                        <ShoppingCart size={32} className="mb-2 opacity-20" />
                        Aucune commande trouvée sur cette page.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= PAGINATION (INTACT) ================= */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <span className="text-sm font-medium text-gray-500">
            {totalOrders} commandes au total | Page {page} sur {totalPages}
          </span>

          <div className="flex space-x-2">
            <button
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
              className="flex items-center px-4 py-2 text-sm font-bold text-gray-700 transition bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} className="mr-1" /> Précédent
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => handlePageChange(page + 1)}
              className="flex items-center px-4 py-2 text-sm font-bold text-gray-700 transition bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================
// MINI STAT CARD (INTACT)
// =========================
function Stat({ label, value, colorClass, Icon }) {
  return (
    <div className={`p-4 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden relative`}>
        <div className={`absolute top-0 right-0 p-1.5 ${colorClass} rounded-bl-xl opacity-70`}>
            {Icon && <Icon size={18} />}
        </div>
        <p className="mb-1 text-xs font-semibold tracking-wider text-gray-500 uppercase">{label}</p>
        <p className="mt-1 text-2xl font-extrabold text-gray-900">{value}</p>
    </div>
  );
}